'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Property {
  id: number;
  name: string;
  location: string;
  country: string;
  type: string;
  price: number;
  currency: string;
  images: { src: string; alt: string }[];
  rating: number;
  reviews: number;
}

interface BookingModalProps {
  property: Property;
  onClose: () => void;
}

type Step = 'dates' | 'guests' | 'review' | 'guestInfo' | 'payment' | 'confirmation';

const STEPS: Step[] = ['dates', 'guests', 'review', 'guestInfo', 'payment', 'confirmation'];
const STEP_LABELS: Record<Step, string> = {
  dates: 'Select Dates',
  guests: 'Guests & Rooms',
  review: 'Review Booking',
  guestInfo: 'Guest Info',
  payment: 'Payment',
  confirmation: 'Confirmation',
};

function generateRef() {
  return 'NDT-' + Math.random().toString(36).substring(2, 8).toUpperCase();
}

export default function BookingModal({ property, onClose }: BookingModalProps) {
  const [step, setStep] = useState<Step>('dates');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const [guestInfo, setGuestInfo] = useState({ firstName: '', lastName: '', email: '', phone: '', specialRequests: '' });
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'mobile' | 'bank'>('card');
  const [cardInfo, setCardInfo] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [mobileNumber, setMobileNumber] = useState('');
  const [bookingRef] = useState(generateRef);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const stepIndex = STEPS.indexOf(step);

  const nights = (() => {
    if (!checkIn || !checkOut) return 0;
    const d1 = new Date(checkIn);
    const d2 = new Date(checkOut);
    const diff = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  })();

  const subtotal = property.price * nights * rooms;
  const cleaningFee = 25000 * rooms;
  const serviceFee = Math.round(subtotal * 0.1);
  const vat = Math.round((subtotal + cleaningFee + serviceFee) * 0.18);
  const total = subtotal + cleaningFee + serviceFee + vat;

  const validateStep = (): boolean => {
    const errs: Record<string, string> = {};
    if (step === 'dates') {
      if (!checkIn) errs.checkIn = 'Check-in date required';
      if (!checkOut) errs.checkOut = 'Check-out date required';
      if (checkIn && checkOut && nights <= 0) errs.checkOut = 'Check-out must be after check-in';
    }
    if (step === 'guestInfo') {
      if (!guestInfo.firstName.trim()) errs.firstName = 'First name required';
      if (!guestInfo.lastName.trim()) errs.lastName = 'Last name required';
      if (!guestInfo.email.trim() || !/\S+@\S+\.\S+/.test(guestInfo.email)) errs.email = 'Valid email required';
      if (!guestInfo.phone.trim()) errs.phone = 'Phone number required';
    }
    if (step === 'payment') {
      if (paymentMethod === 'card') {
        if (!cardInfo.number.trim()) errs.cardNumber = 'Card number required';
        if (!cardInfo.expiry.trim()) errs.expiry = 'Expiry required';
        if (!cardInfo.cvv.trim()) errs.cvv = 'CVV required';
        if (!cardInfo.name.trim()) errs.cardName = 'Name on card required';
      }
      if (paymentMethod === 'mobile' && !mobileNumber.trim()) {
        errs.mobile = 'Mobile number required';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const back = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{STEP_LABELS[step]}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Step {stepIndex + 1} of {STEPS.length}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-[#4088C6] transition-all duration-500"
            style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-50 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < stepIndex ? 'bg-[#4088C6] text-white' :
                i === stepIndex ? 'bg-gray-900 text-white': 'bg-gray-100 text-gray-400'
              }`}>
                {i < stepIndex ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-6 h-px ${i < stepIndex ? 'bg-[#4088C6]/50' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Property summary strip */}
          {step !== 'confirmation' && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl mb-5">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                <AppImage src={property.images[0].src} alt={property.images[0].alt} width={56} height={56} className="object-cover w-full h-full" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{property.name}</p>
                <p className="text-xs text-gray-500">{property.location}, {property.country}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-gray-900">{property.currency} {property.price.toLocaleString()}</p>
                <p className="text-xs text-gray-500">/ night</p>
              </div>
            </div>
          )}

          {/* ── STEP: Dates ── */}
          {step === 'dates' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Check-in</label>
                  <input
                    type="date"
                    min={today}
                    value={checkIn}
                    onChange={(e) => { setCheckIn(e.target.value); setErrors({}); }}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.checkIn ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.checkIn && <p className="text-xs text-red-500 mt-1">{errors.checkIn}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Check-out</label>
                  <input
                    type="date"
                    min={checkIn || today}
                    value={checkOut}
                    onChange={(e) => { setCheckOut(e.target.value); setErrors({}); }}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.checkOut ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.checkOut && <p className="text-xs text-red-500 mt-1">{errors.checkOut}</p>}
                </div>
              </div>
              {nights > 0 && (
                <div className="bg-[#4088C6]/10 border border-[#4088C6]/20 rounded-xl px-4 py-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#4088C6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-[#4088C6]">{nights} night{nights !== 1 ? 's' : ''} selected</span>
                </div>
              )}
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-amber-700 mb-1">Cancellation Policy</p>
                <p className="text-xs text-amber-600">Free cancellation before 48 hours of check-in. After that, the first night is non-refundable.</p>
              </div>
            </div>
          )}

          {/* ── STEP: Guests & Rooms ── */}
          {step === 'guests' && (
            <div className="space-y-4">
              {[
                { label: 'Adults', sub: 'Ages 13+', value: adults, set: setAdults, min: 1, max: 10 },
                { label: 'Children', sub: 'Ages 2–12', value: children, set: setChildren, min: 0, max: 8 },
                { label: 'Rooms', sub: 'Number of rooms', value: rooms, set: setRooms, min: 1, max: 5 },
              ].map(({ label, sub, value, set, min, max }) => (
                <div key={label} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{label}</p>
                    <p className="text-xs text-gray-500">{sub}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => set(Math.max(min, value - 1))}
                      disabled={value <= min}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-gray-900">{value}</span>
                    <button
                      onClick={() => set(Math.min(max, value + 1))}
                      disabled={value >= max}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    </button>
                  </div>
                </div>
              ))}
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">Summary: </span>
                  {adults + children} guest{adults + children !== 1 ? 's' : ''} · {rooms} room{rooms !== 1 ? 's' : ''}
                  {nights > 0 && ` · ${nights} night${nights !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
          )}

          {/* ── STEP: Review Booking ── */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-bold text-gray-900">Booking Summary</h3>
                </div>
                <div className="px-4 py-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Check-in</span>
                    <span className="font-semibold text-gray-900">{checkIn || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Check-out</span>
                    <span className="font-semibold text-gray-900">{checkOut || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-semibold text-gray-900">{nights} night{nights !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Guests</span>
                    <span className="font-semibold text-gray-900">{adults + children} ({adults} adults{children > 0 ? `, ${children} children` : ''})</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Rooms</span>
                    <span className="font-semibold text-gray-900">{rooms}</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-bold text-gray-900">Price Breakdown</h3>
                </div>
                <div className="px-4 py-4 space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">UGX {property.price.toLocaleString()} × {nights} nights × {rooms} room{rooms !== 1 ? 's' : ''}</span>
                    <span className="text-gray-900">UGX {subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cleaning fee</span>
                    <span className="text-gray-900">UGX {cleaningFee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service fee (10%)</span>
                    <span className="text-gray-900">UGX {serviceFee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (18%)</span>
                    <span className="text-gray-900">UGX {vat.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2.5 flex justify-between">
                    <span className="text-sm font-bold text-gray-900">Total</span>
                    <span className="text-sm font-bold text-gray-900">UGX {total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: Guest Info ── */}
          {step === 'guestInfo' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">First Name</label>
                  <input
                    type="text"
                    placeholder="John"
                    value={guestInfo.firstName}
                    onChange={(e) => setGuestInfo({ ...guestInfo, firstName: e.target.value })}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.firstName ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Last Name</label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={guestInfo.lastName}
                    onChange={(e) => setGuestInfo({ ...guestInfo, lastName: e.target.value })}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.lastName ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={guestInfo.email}
                  onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                  className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.email ? 'border-red-400' : 'border-gray-200'}`}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  placeholder="+256 700 000 000"
                  value={guestInfo.phone}
                  onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                  className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.phone ? 'border-red-400' : 'border-gray-200'}`}
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Special Requests <span className="font-normal text-gray-400">(optional)</span></label>
                <textarea
                  rows={3}
                  placeholder="Any special requirements or requests..."
                  value={guestInfo.specialRequests}
                  onChange={(e) => setGuestInfo({ ...guestInfo, specialRequests: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 resize-none"
                />
              </div>
            </div>
          )}

          {/* ── STEP: Payment ── */}
          {step === 'payment' && (
            <div className="space-y-4">
              {/* Method selector */}
              <div className="grid grid-cols-3 gap-3">
                {([
                  { id: 'card', label: 'Credit Card', icon: '💳' },
                  { id: 'mobile', label: 'Mobile Money', icon: '📱' },
                  { id: 'bank', label: 'Bank Transfer', icon: '🏦' },
                ] as const).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPaymentMethod(m.id)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${
                      paymentMethod === m.id
                        ? 'border-[#4088C6] bg-[#4088C6]/5' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">{m.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{m.label}</span>
                  </button>
                ))}
              </div>

              {paymentMethod === 'card' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Card Number</label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      value={cardInfo.number}
                      onChange={(e) => setCardInfo({ ...cardInfo, number: e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim() })}
                      className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.cardNumber ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {errors.cardNumber && <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Expiry Date</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        maxLength={5}
                        value={cardInfo.expiry}
                        onChange={(e) => setCardInfo({ ...cardInfo, expiry: e.target.value })}
                        className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.expiry ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {errors.expiry && <p className="text-xs text-red-500 mt-1">{errors.expiry}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">CVV</label>
                      <input
                        type="text"
                        placeholder="123"
                        maxLength={4}
                        value={cardInfo.cvv}
                        onChange={(e) => setCardInfo({ ...cardInfo, cvv: e.target.value.replace(/\D/g, '') })}
                        className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.cvv ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {errors.cvv && <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Name on Card</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={cardInfo.name}
                      onChange={(e) => setCardInfo({ ...cardInfo, name: e.target.value })}
                      className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.cardName ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {errors.cardName && <p className="text-xs text-red-500 mt-1">{errors.cardName}</p>}
                  </div>
                </div>
              )}

              {paymentMethod === 'mobile' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Money Number</label>
                    <input
                      type="tel"
                      placeholder="+256 700 000 000"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 ${errors.mobile ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                  </div>
                  <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3">
                    <p className="text-xs text-yellow-700">You will receive a payment prompt on your mobile phone. Approve the transaction to complete your booking.</p>
                  </div>
                </div>
              )}

              {paymentMethod === 'bank' && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-4 space-y-2">
                  <p className="text-sm font-bold text-blue-900">Bank Transfer Details</p>
                  <div className="space-y-1.5 text-sm text-blue-800">
                    <div className="flex justify-between"><span className="text-blue-600">Bank:</span><span className="font-semibold">Stanbic Bank Uganda</span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Account Name:</span><span className="font-semibold">Ndoto Properties Ltd</span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Account No:</span><span className="font-semibold">9030012345678</span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Reference:</span><span className="font-semibold">{bookingRef}</span></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Use the booking reference as your payment reference. Your booking will be confirmed within 24 hours.</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Your payment information is encrypted and secure.
              </div>

              <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
                <span className="text-sm font-bold text-gray-900">Total due</span>
                <span className="text-lg font-bold text-rose-600">UGX {total.toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* ── STEP: Confirmation ── */}
          {step === 'confirmation' && (
            <div className="text-center py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Booking Confirmed!</h3>
              <p className="text-gray-500 text-sm mb-5">Your reservation has been successfully placed.</p>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 mb-5 inline-block">
                <p className="text-xs text-gray-500 mb-1">Booking Reference</p>
                <p className="text-2xl font-bold text-gray-900 tracking-widest">{bookingRef}</p>
              </div>

              <div className="border border-gray-200 rounded-2xl overflow-hidden text-left mb-5">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900">Booking Details</h4>
                </div>
                <div className="px-4 py-4 space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Property</span>
                    <span className="font-semibold text-gray-900 text-right max-w-[60%]">{property.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Guest</span>
                    <span className="font-semibold text-gray-900">{guestInfo.firstName} {guestInfo.lastName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Check-in</span>
                    <span className="font-semibold text-gray-900">{checkIn}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Check-out</span>
                    <span className="font-semibold text-gray-900">{checkOut}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Guests</span>
                    <span className="font-semibold text-gray-900">{adults + children}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Rooms</span>
                    <span className="font-semibold text-gray-900">{rooms}</span>
                  </div>
                  <div className="border-t border-gray-100 pt-2.5 flex justify-between">
                    <span className="text-sm font-bold text-gray-900">Total Paid</span>
                    <span className="text-sm font-bold text-gray-900">UGX {total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                A confirmation email has been sent to <span className="font-semibold text-gray-700">{guestInfo.email || 'your email'}</span>
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          {step !== 'confirmation' ? (
            <>
              <button
                onClick={stepIndex === 0 ? onClose : back}
                className="px-5 py-2.5 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {stepIndex === 0 ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={next}
                className="flex-1 max-w-xs px-5 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold transition-colors"
              >
                {step === 'payment' ? `Pay UGX ${total.toLocaleString()}` : 'Continue'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="w-full px-5 py-2.5 rounded-full bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
